import { useEffect, useMemo } from "react";
import { ResourceCostResponse } from "rwb/pages/project/types/interfaces/costs";
import useUser from "~/contexts/user";

export const VITE_COST_AND_USAGE_API = import.meta.env.VITE_COST_AND_USAGE_API;

// Shared by every cost-and-usage endpoint (compute env, run, submission, runs list) -- don't
// hammer the API with retries on failure.
export const COST_SWR_BASE_CONFIG = {
  errorRetryCount: 0,
  errorRetryInterval: 0,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

// Once a run or submission completes, its cost never changes -- skip revalidating stale data
// (2hr SWR cache) to avoid redundant requests. Compute env cost accrues while running, so that
// hook uses COST_SWR_BASE_CONFIG directly instead.
export const IMMUTABLE_COST_SWR_CONFIG = {
  ...COST_SWR_BASE_CONFIG,
  revalidateIfStale: false,
};

export const useCostAuthHeaders = () => {
  const user = useUser();
  return { "x-sc-auth-identity": user?.email ?? "" };
};

export const appendDateRangeParams = (
  params: URLSearchParams,
  startDate?: string,
  endDate?: string
) => {
  if (startDate) {
    params.set("start_date", startDate);
  }
  if (endDate) {
    params.set("end_date", endDate);
  }
};

export interface PaginatedCostPage<TCost extends ResourceCostResponse> {
  items: TCost[];
  metadata: { hasMore: boolean };
}

export interface UsePaginatedCostMapParams<TCost extends ResourceCostResponse> {
  data?: PaginatedCostPage<TCost>[];
  size: number;
  setSize: (size: number) => void;
}

// Shared by every paginated cost-and-usage list (compute envs, runs)
// SWRInfinite only fetches one page per setSize call, so this walks pages until hasMore is false
export const usePaginatedCostMap = <TCost extends ResourceCostResponse>({
  data,
  size,
  setSize,
}: UsePaginatedCostMapParams<TCost>) => {
  const isAllCostLoaded = !!data && !data[data.length - 1]?.metadata.hasMore;

  useEffect(() => {
    const lastPage = data?.[data.length - 1];
    if (data?.length === size && lastPage?.metadata.hasMore) {
      setSize(size + 1);
    }
  }, [data, size, setSize]);

  const costByResourceId = useMemo(() => {
    const map = new Map<string, TCost>();
    for (const page of data ?? []) {
      for (const item of page.items) {
        map.set(item.resourceId, item);
      }
    }
    return map;
  }, [data]);

  return { costByResourceId, isAllCostLoaded };
};
